import React, { useState, useEffect, useRef } from "react";
import { Button, Modal, Input, Typography, Space, Spin, Tag, Popconfirm, AutoComplete, message, Tooltip } from "antd";
import {
  SearchOutlined,
  RightOutlined,
  SendOutlined,
  FormOutlined, DeleteOutlined
} from "@ant-design/icons";
import axios from "axios";
import { routePath } from "../../static/routes.static";
import { useNavigate, useLocation } from "react-router-dom";
import { dataFormMetaData } from "../../static/formRoutes";
import { useDispatch, useSelector } from "react-redux";
import { selectTheme } from "../../redux/features/appSlice";
import { db } from "../../firebase/setup";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { selectUserDoc } from "../../redux/features/userSlice";

const { Text, Title, Paragraph } = Typography;

// Local storage key
const CHAT_HISTORY_KEY = "sustrax_ai_chat_history";

// Route descriptions for better user experience
const routesConfig = {
  "/dashboard": "View your main dashboard with key metrics and statistics",
  "/profile": "Manage your profile and account settings",
  "/companies": "View and manage all companies",
  "/sites": "View and manage all sites",
  "/users": "Manage user accounts and permissions",
  "/tasks": "View and manage assigned tasks",
  "/company/:id": "View detailed company information",
  "/apps": "Access available applications and tools",
  "/site/:id": "View detailed site information",
  "/form": "Submit new data entry forms for sustainability reporting",
  "/bulk-upload": "Upload multiple data entries using CSV files",
  "/results": "View emission results, export data and view charts",
  "/combined_data_table": "View combined data table with all sustainability metrics",
  "/analysis": "View detailed sustainability emissions analysis",
  "/analysis": "Publish your results to the Carbon Database Initiative (Carbondi.com)",
  "/logout": "Log out of your account",
  "/faq": "View frequently asked questions",
  "/upgrade": "Upgrade your subscription plan",
  "/offset": "Explore carbon offset options",
  "/learn": "Access various tutorials",
};

const generateOtherQuestions = (dispatch) => [
  { id: "email_change", question: "How do I change my email?", answer: "Please contact support to change your email as this cannot be done independently." },
  {
    id: "dark_mode",
    question: "How do I toggle dark/light mode?",
    answer: "You can toggle dark/light mode using the floating button at the bottom right or through the header menu. Alternatively, you can toggle it here:",
    onClick: () => {
      document.dispatchEvent(new Event('toggleDarkMode'));
    },
    buttonTitle: "Toggle Dark Mode"
  },
  {
    id: "bug_report",
    question: "Where can I report a bug?",
    answer: "You can report a bug by clicking on the profile avatar in the header and selecting 'Report a bug'. Alternatively, you can report it here:",
    onClick: () => {
      document.dispatchEvent(new Event('openReportBug'));
    },
    buttonTitle: "Report a Bug"
  },
  {
    id: "request_feature",
    question: "Where can I request a new feature?",
    answer: "You can request a new feature by clicking on the profile avatar in the header. Alternatively, you can request it here:",
    onClick: () => {
      document.dispatchEvent(new Event('openRequestFeature'));
    },
    buttonTitle: "Request Feature"
  },
  {
    id: "translate",
    question: "Where can I translate the site?",
    answer: "You can translate the site by clicking on the profile avatar in the header. Alternatively, you can translate it here:",
    onClick: () => {
      document.dispatchEvent(new Event('openTranslate'));
    },
    buttonTitle: "Translate Site"
  },
  {
    id: "accessibility",
    question: "Where can I change my font, font size and accessibility options?",
    answer: "You can access accessibility settings by clicking on the profile avatar in the header. Alternatively, you can access them here:",
    onClick: () => {
      document.dispatchEvent(new Event('openAccessibility'));
    },
    buttonTitle: "Accessibility Settings"
  }
]

const saveChatToFirestore = async (userId, userEmail, query, response, currentPath) => {
  try {
    const now = new Date();

    // Create a document in the ChatbotHistory collection
    await addDoc(collection(db, "ChatbotHistory"), {
      userId: userId,
      userEmail: userEmail,
      query: query,
      response: response,
      currentPath: currentPath,
      timestamp: serverTimestamp(),
      // Additional time fields for easier querying
      year: now.getFullYear(),
      month: now.getMonth() + 1, // JavaScript months are 0-indexed
      day: now.getDate(),
      hour: now.getHours(),
      minute: now.getMinutes(),
      timeString: now.toLocaleTimeString(),
      dateString: now.toLocaleDateString()
    });

    console.log("Chat saved to Firestore successfully");
  } catch (error) {
    console.error("Error saving chat to Firestore:", error);
  }
};

const AIsearch = () => {


  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const navigate = useNavigate();
  const [routesData, setRoutesData] = useState([]);
  const chatContainerRef = useRef(null);
  const [suggestions, setSuggestions] = useState([]);
  const [searchVisible, setSearchVisible] = useState(false);
  const location = useLocation();

  const dispatch = useDispatch();
  const { isDarkMode } = useSelector(selectTheme);
  const user = useSelector(selectUserDoc);



  const otherQuestions = generateOtherQuestions(dispatch);


  // Load chat history from localStorage when component mounts
  useEffect(() => {
    try {
      const savedHistory = localStorage.getItem(CHAT_HISTORY_KEY);
      if (savedHistory) {
        setChatHistory(JSON.parse(savedHistory));
      }
    } catch (error) {
      console.error("Error loading chat history from localStorage:", error);
    }
  }, []);

  // Save chat history to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(CHAT_HISTORY_KEY, JSON.stringify(chatHistory));
    } catch (error) {
      console.error("Error saving chat history to localStorage:", error);
    }
  }, [chatHistory]);

  // Scroll to bottom whenever chat history changes
  useEffect(() => {
    if (chatContainerRef.current && isModalOpen) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatHistory, isModalOpen]);

  // Prepare routes data when component mounts
  useEffect(() => {
    // Combine all routes into a single array with descriptions
    const combinedRoutes = [
      // Regular routes
      ...Object.entries(routesConfig).map(([key, value]) => ({
        path: key,
        title: getRouteTitle(key),
        description: value || getDefaultDescription(key),
        type: "route"
      })),

      // // Static routes from routePath
      // ...Object.entries(routePath)
      //   .filter(([key, path]) => typeof path === 'string')
      //   .map(([key, path]) => ({
      //     path,
      //     title: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
      //     description: routesConfig[path] || `Access the ${key} section`,
      //     type: "route"
      //   })),

      // Form routes
      ...dataFormMetaData.map(form => ({
        path: `/form?name=${form.name}`,
        title: form.title,
        description: form.description || `Enter ${form.title} data for carbon footprint calculation`,
        type: "form",
        formName: form.name
      }))
    ];

    // Remove duplicates
    const uniquePaths = new Set();
    const uniqueRoutes = combinedRoutes.filter(route => {
      if (uniquePaths.has(route.path)) {
        return false;
      }
      uniquePaths.add(route.path);
      return true;
    });

    setRoutesData(uniqueRoutes);
  }, []);

  const showModal = () => {
    setIsModalOpen(true);
    setSearchQuery("");
    // Don't reset chat history anymore since we're persisting it
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const clearChatHistory = () => {
    setChatHistory([]);
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Generate suggestions based on user input
  const generateSuggestions = (value) => {
    if (!value) {
      setSuggestions([]);
      return;
    }

    const inputValue = value.toLowerCase();
    const commonQueries = [
      "How do I add a new company?",
      "Where can I view my carbon footprint?",
      "How do I upload data?",
      "How do I generate reports?",
      "Where is the dashboard?",
      "How do I change my password?",
      "How do I add a new site?",
      "How do I toggle dark mode?",
      "Where can I find help?",
      "How do I export data?"
    ];

    const filtered = commonQueries.filter(
      query => query.toLowerCase().includes(inputValue)
    );

    setSuggestions(filtered.slice(0, 5).map(query => ({ value: query })));
  };

  const handleSearch = async (value) => {
    if (!value.trim()) return;

    setSearchQuery("");
    setSuggestions([]); // Clear suggestions after search
    setLoading(true);

    // Add user message to chat history with timestamp
    const newUserMessage = {
      type: 'user',
      content: value,
      timestamp: new Date().toISOString()
    };

    setChatHistory(prev => [...prev, newUserMessage]);

    try {
      // Instructions for the AI with specific format request
      const instructions = `
        You are a helpful assistant for Sustrax MX, a carbon footprint measuring tool.
        Help users navigate to the right page or form in the application based on their query.
        
        IMPORTANT RESPONSE FORMAT INSTRUCTIONS:
        
        For navigation questions, format your response like this:
        "You can find your company information in the Companies section. This page shows all companies registered in the system and allows you to manage them.
        
        ROUTE:/companies"
        
        For specific questions about these topics, DO NOT include a ROUTE. Instead, include the question ID at the end:
        ${JSON.stringify(otherQuestions)}
        
        Example for a specific question:
        "Dark mode is present on the header. You can also toggle it here.
        
        QUESTION_ID:dark_mode"
        
        DO NOT include ROUTE: for any of the specific questions listed above.
        Always use QUESTION_ID: for the predefined questions.
        
        Available routes and forms: ${JSON.stringify(routesData)}
        
        Be conversational and helpful, explaining what the user can do at the suggested location.
      `;

      // Make API call to the AI endpoint
      const response = await axios.post('https://bot-alistermartin.pythonanywhere.com/simple_ai_query', {
        instructions,
        query: value
      });

      if (response.data && response.data.resp_text) {
        const aiResponse = response.data.resp_text.trim();

        // Extract the route using the ROUTE: format
        const routeMatch = aiResponse.match(/ROUTE:([^\n]+)/);
        // Extract the question ID using the QUESTION_ID: format
        const questionIdMatch = aiResponse.match(/QUESTION_ID:([^\s]+)/);

        let routePath = null;
        let explanation = aiResponse;
        let questionId = null;
        let newAiMessage = null;

        if (routeMatch && routeMatch[1]) {
          routePath = routeMatch[1].trim();
          // Remove the ROUTE: line from the explanation
          explanation = aiResponse.replace(/ROUTE:[^\n]+/, '').trim();
        }

        if (questionIdMatch && questionIdMatch[1]) {
          questionId = questionIdMatch[1].trim();
          // Remove the QUESTION_ID: line from the explanation
          explanation = aiResponse.replace(/QUESTION_ID:[^\n]+/, '').trim();

          // Find the matching question by ID
          const matchedQuestion = otherQuestions.find(q => q.id === questionId);

          if (matchedQuestion) {
            // Add AI response to chat history with timestamp and button if needed
            newAiMessage = {
              type: 'ai',
              content: explanation,
              hasButton: !!matchedQuestion.onClick,
              onClick: matchedQuestion.onClick,
              buttonTitle: matchedQuestion.buttonTitle || "Click here",
              timestamp: new Date().toISOString()
            };
            setChatHistory(prev => [...prev, newAiMessage]);
          } else {
            // If no matching question found, just show the explanation
            newAiMessage = {
              type: 'ai',
              content: explanation,
              timestamp: new Date().toISOString()
            };
            setChatHistory(prev => [...prev, newAiMessage]);
          }
        } else if (routePath) {
          // Find the matching route in our prepared data
          let matchedRoute = routesData.find(route =>
            route.path === routePath ||
            (routePath.includes('/') && route.path === routePath)
          );

          // Add AI response to chat history with timestamp
          if (matchedRoute) {
            newAiMessage = {
              type: 'ai',
              content: explanation,
              route: matchedRoute,
              timestamp: new Date().toISOString()
            };
            setChatHistory(prev => [...prev, newAiMessage]);
          } else {
            // If no route found, just show the explanation
            newAiMessage = {
              type: 'ai',
              content: explanation,
              timestamp: new Date().toISOString()
            };
            setChatHistory(prev => [...prev, newAiMessage]);
          }
        } else {
          // If no route or question ID found, just show the explanation
          newAiMessage = {
            type: 'ai',
            content: explanation,
            timestamp: new Date().toISOString()
          };
          setChatHistory(prev => [...prev, newAiMessage]);

          // Try to find relevant routes based on keywords
          const keywords = value.toLowerCase().split(/\s+/).filter(word => word.length > 3);

          const relevantRoutes = routesData
            .filter(route =>
              keywords.some(keyword =>
                route.title.toLowerCase().includes(keyword) ||
                route.description.toLowerCase().includes(keyword)
              )
            )
            .slice(0, 3);

          if (relevantRoutes.length > 0) {
            const routesMessage = {
              type: 'ai',
              content: "Here are some pages that might be relevant:",
              routes: relevantRoutes,
              timestamp: new Date().toISOString()
            };
            setChatHistory(prev => [...prev, routesMessage]);
          }
        }

        // Save chat to Firebase

        console.log("saving history in firebase", user)
        if (user && user.id) {
          await saveChatToFirestore(
            user.id,
            user.email || 'anonymous',
            value,
            explanation,
            location.pathname
          );
        }
      } else {
        message.info("Error saving history. Please try again.");
      }
    } catch (error) {
      console.error("Error querying AI:", error);
      const errorMessage = {
        type: 'ai',
        content: `Sorry, I encountered an error. Please try again.`,
        timestamp: new Date().toISOString()
      };
      setChatHistory(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const getRouteTitle = (path) => {
    // Remove leading slash and convert to title case
    return path.replace(/^\//, '')
      .split('/')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ')
      .replace(/\?.*$/, '') // Remove query parameters
      .replace(/-/g, ' ')
      .replace(/_/g, ' ');
    // replace dash and underscore with space
  };

  const getDefaultDescription = (path) => {
    return `Navigate to ${getRouteTitle(path)}`;
  };

  const navigateTo = (path) => {
    console.log(`Navigating to: ${path}`);
    // Use React Router's navigate instead of window.location
    navigate(path);
    setIsModalOpen(false);
  };

  const renderRouteCard = (route) => (
    <div
      onClick={() => navigateTo(route.path)}
      style={{
        cursor: 'pointer',
        padding: '12px',
        borderRadius: '8px',
        marginTop: '8px',
        backgroundColor: isDarkMode ? '#1f1f1f' : '#fff',
        border: `1px solid ${isDarkMode ? '#303030' : '#e8e8e8'}`,
        transition: 'all 0.3s'
      }}
      className="hover-card"
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={5} style={{ margin: 0, color: isDarkMode ? '#fff' : 'inherit' }}>
          {route.type === 'form' ? (
            <Space>
              <FormOutlined />
              {route.title}
            </Space>
          ) : (
            route.title
          )}
        </Title>
        <RightOutlined style={{ color: isDarkMode ? '#fff' : 'inherit' }} />
      </div>
      <Text type="secondary">{route.description}</Text>
      <Tag color={route.type === 'form' ? 'green' : 'blue'} style={{ marginTop: '8px' }}>
        {route.path}
      </Tag>
    </div>
  );

  return (
    <div style={{ marginRight: "3px" }}>
      {/* Compact search button for header */}
      <div
        onClick={showModal}
        style={{
          display: 'flex',
          alignItems: 'center',
          border: `1px solid ${isDarkMode ? '#303030' : '#d9d9d9'}`,
          borderRadius: '4px',
          padding: '2px 8px',
          cursor: 'pointer',
          backgroundColor: isDarkMode ? '#1f1f1f' : '#fff',
          width: '160px',
          height: '28px'
        }}
      >
        <SearchOutlined style={{ color: isDarkMode ? '#bfbfbf' : 'rgba(0, 0, 0, 0.45)', marginRight: '4px' }} />
        <span style={{
          color: isDarkMode ? '#bfbfbf' : 'rgba(0, 0, 0, 0.45)',
          fontSize: '12px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          Search Sustrax AI
        </span>
      </div>

      <Modal
        title={
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', borderBottom: `1px solid ${isDarkMode ? '#303030' : '#e8e8e8'}` }}>
              <span>Sustrax Chat</span>
              {/* <span style={{ fontSize: '12px', color: isDarkMode ? '#bfbfbf' : '#999' }}>Search for any route, functions or any questions</span> */}
            </div>
            <Space>
              {chatHistory.length > 0 && (
                <Popconfirm
                  title="Clear chat?"
                  onConfirm={clearChatHistory}
                  okText="Yes"
                >
                  <Button
                    icon={<DeleteOutlined />}
                    size="small"
                    type="text"
                  >
                  </Button>
                </Popconfirm>
              )}
            </Space>
          </div>
        }
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
        width={500}
        styles={{
          maxHeight: '70vh',
          overflow: 'auto',
          backgroundColor: isDarkMode ? '#141414' : '#fff',
          padding: '16px',
          borderRadius: '8px'
        }}
      >
        <div
          className="chat-container"
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '400px',
            marginBottom: '16px',
            overflow: 'auto',
            padding: '8px',
            borderRadius: '8px',
            backgroundColor: isDarkMode ? '#141414' : '#fff'
          }}
          ref={chatContainerRef}
        >
          {chatHistory.length === 0 ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: isDarkMode ? '#bfbfbf' : '#888',
              textAlign: 'center',
              padding: '0 20px'
            }}>
              <SearchOutlined style={{ fontSize: '32px', marginBottom: '16px' }} />
              <Paragraph style={{ color: isDarkMode ? '#bfbfbf' : 'inherit' }}>
                I'm here to help you navigate the platform, find specific actions, and answer your questions.
              </Paragraph>
              <Paragraph type="secondary">
                Try asking: "How do I view my companies?", "Where can I enter electricity consumption data?", or "Show me my sustainability results"
              </Paragraph>
            </div>
          ) : (
            chatHistory.map((message, index) => (
              <div
                key={index}
                style={{
                  alignSelf: message.type === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '80%',
                  marginBottom: '12px'
                }}
              >
                <div style={{
                  backgroundColor: message.type === 'user'
                    ? '#1890ff'
                    : isDarkMode ? '#1f1f1f' : '#fff',
                  color: message.type === 'user'
                    ? '#fff'
                    : isDarkMode ? '#fff' : '#000',
                  padding: '10px 14px',
                  borderRadius: '18px',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                  border: message.type === 'user'
                    ? 'none'
                    : `1px solid ${isDarkMode ? '#303030' : '#e8e8e8'}`
                }}>
                  {message.content}
                </div>

                {/* Timestamp */}
                <div style={{
                  fontSize: '11px',
                  color: isDarkMode ? '#bfbfbf' : '#999',
                  marginTop: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start'
                }}>
                  {message.timestamp && formatTimestamp(message.timestamp)}
                </div>

                {/* Add button if hasButton is true */}
                {message.hasButton && (
                  <div style={{ marginTop: '8px' }}>
                    <Button
                      type="primary"
                      onClick={typeof message.onClick === 'function' ? message.onClick : () => {
                        otherQuestions.find(q => q.buttonTitle === message.buttonTitle)?.onClick();
                      }}
                    >
                      {message.buttonTitle || "Click here"}
                    </Button>
                  </div>
                )}

                {message.route && renderRouteCard(message.route)}

                {message.routes && (
                  <div style={{ marginTop: '8px' }}>
                    {message.routes.map((route, idx) => renderRouteCard(route))}
                  </div>
                )}
              </div>
            ))
          )}

          {loading && (
            <div style={{ alignSelf: 'flex-start', marginBottom: '12px' }}>
              <div style={{
                backgroundColor: isDarkMode ? '#1f1f1f' : '#fff',
                padding: '10px 14px',
                borderRadius: '18px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                border: `1px solid ${isDarkMode ? '#303030' : '#e8e8e8'}`
              }}>
                <Spin size="small" />
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex' }}>
          <AutoComplete
            style={{ width: '100%' }}
            options={suggestions}
            value={searchQuery}
            onChange={setSearchQuery}
            onSearch={(text) => generateSuggestions(text)}
            onSelect={(value) => {
              setSearchQuery(value);
              handleSearch(value);
            }}
            placeholder="Ask anything..."
            autoFocus={true}
            dropdownMatchSelectWidth={true}
          >
            <Input.Search
              size="large"
              onSearch={handleSearch}
              disabled={loading}
              enterButton={
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  disabled={!searchQuery.trim() || loading}
                >
                  Send
                </Button>
              }
            />
          </AutoComplete>
        </div>

        {/* AI Disclaimer */}
        <Tooltip title="This AI chatbot is a great first step in learning about Sustrax. It has been trained on the layout, inputs and features of the system, but it might not always have the most up-to-date details, accuracy, or knowledge of future developments. If you need more specific help, have questions the chatbot can't answer, or want to learn about the latest features, please don't hesitate to contact your Sustrax account manager. They are always happy to assist you.">

          <div
            style={{
              marginTop: '8px',
              textAlign: 'left',
              color: isDarkMode ? '#8c8c8c' : '#999',
              fontSize: '10px',
              lineHeight: '1.4',
              cursor: 'help'
            }}
          >
            AI can make mistakes. Your personal information and company data is not sent to AI. See FAQs for more details.
          </div>
        </Tooltip>
      </Modal>

      <style jsx>{`
        .hover-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 2px 8px rgba(0,0,0,${isDarkMode ? '0.2' : '0.09'});
        }
      `}</style>
    </div>
  );
};

export default AIsearch;